/**
 * Token 管理工具模块
 * 负责token的存储、获取、验证和清理
 */

const TOKEN_KEY = 'token';
const TOKEN_EXPIRE_TIME_KEY = 'tokenExpireTime';

/**
 * 保存 token 和过期时间
 * @param token - 访问令牌
 * @param expiresIn - 过期时间（秒）
 */
export const saveToken = (token: string, expiresIn?: number): void => {
  localStorage.setItem(TOKEN_KEY, token);
  if (expiresIn) {
    const expireTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(TOKEN_EXPIRE_TIME_KEY, String(expireTime));
  }
};

/**
 * 获取 token
 * @returns token 字符串，如果不存在则返回 null
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 获取 token 过期时间戳
 * @returns 过期时间戳（毫秒），如果不存在则返回 null
 */
export const getTokenExpireTime = (): number | null => {
  const expireTime = localStorage.getItem(TOKEN_EXPIRE_TIME_KEY);
  return expireTime ? Number(expireTime) : null;
};

/**
 * 检查 token 是否过期
 * @returns true 表示已过期或不存在，false 表示有效
 */
export const isTokenExpired = (): boolean => {
  const expireTime = getTokenExpireTime();
  if (!expireTime) {
    // 没有过期时间，认为 token 有效
    return false;
  }
  // 提前 30 秒判断为过期，避免边界情况
  return Date.now() >= expireTime - 30000;
};

/**
 * 检查 token 是否有效
 * @returns true 表示有效，false 表示无效或过期
 */
export const isTokenValid = (): boolean => {
  const token = getToken();
  if (!token) {
    return false;
  }
  return !isTokenExpired();
};

/**
 * 清除 token 和过期时间
 */
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRE_TIME_KEY);
};

/** 即将过期提示阈值(秒):剩余 5 分钟内提示一次 */
export const TOKEN_EXPIRING_THRESHOLD_SECONDS = 300;

export type TokenNotice = 'none' | 'expiring' | 'expired';

/**
 * 过期检测判定(纯函数,时间源经 now 注入便于单测)
 * - 无过期时间(登录响应未带 expiresIn):与 isTokenExpired 口径一致视为有效,
 *   不做前端过期提示,交由接口 401 兜底;
 * - 已过期 → 'expired';
 * - 进入阈值且尚未提示过 → 'expiring'(只提示一次,但检测继续直到过期)。
 */
export const getTokenNotice = (
  expireTime: number | null,
  now: number,
  warned: boolean,
): TokenNotice => {
  if (!expireTime) return 'none';
  const remainingMs = expireTime - now;
  if (remainingMs <= 0) return 'expired';
  if (!warned && remainingMs <= TOKEN_EXPIRING_THRESHOLD_SECONDS * 1000) {
    return 'expiring';
  }
  return 'none';
};

/** 剩余分钟数(向上取整且至少 1,避免出现「0 分钟后过期」) */
export const getRemainingMinutes = (expireTime: number, now: number): number =>
  Math.max(1, Math.ceil((expireTime - now) / 60000));

/**
 * 获取 token 剩余有效时间（秒）
 * @returns 剩余秒数，如果已过期或不存在则返回 0
 */
export const getTokenRemainingTime = (): number => {
  const expireTime = getTokenExpireTime();
  if (!expireTime) {
    return 0;
  }
  const remaining = Math.max(0, expireTime - Date.now());
  return Math.floor(remaining / 1000);
};
